import { Injectable } from '@angular/core';
import { FilterItem } from '@core/interface/filter-item';
import { TenantStateService } from './tenant-state/tenant-state.service';
import { UserStateService } from './user-state/user-state.service';
import { ERoleName } from '@core/api';
import { EPossibleActionsEnum } from '@core/enum/possible-actions-enum';
import { KibanaUrlParams } from '@core/interface/kibana-url-params';
import rison from 'rison';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';

@Injectable({
  providedIn: 'root'
})
export class UtilService {
  constructor(
    private tenantStateService: TenantStateService,
    private userStateService: UserStateService,
    private sanitizer: DomSanitizer
  ) {}

  changeItemToJsonString(filterItems?: FilterItem[]): string {
    filterItems?.forEach(filter => {
      if (filter.Value === null) filterItems.splice(filterItems.indexOf(filter), 1);
      else filter.Value = filter.Value.toString();
    });
    return JSON.stringify(filterItems);
  }

  capitilizeFirstLetter(value?: string): string | undefined {
    if (!value) return value;
    return value.charAt(0).toUpperCase() + value.slice(1);
  }

  // Function to safely access item values dynamically
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getItemValue(item: any, key: string): string {
    // Split the key by '.' to handle nested properties
    const value = key.split('.').reduce((acc, part) => acc && acc[part], item);

    // Check if the final value is undefined or null
    if (value === undefined || value === null) {
      return 'N/A'; // Provide a default value for undefined or null keys
    } else if (typeof value === 'object') {
      return JSON.stringify(value); // Convert objects to JSON string if necessary
    }
    return value;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setDeleteActionPerUserRole(item: any, action: EPossibleActionsEnum): boolean {
    if (item.roleName === ERoleName.Fisherman && action === EPossibleActionsEnum.DELETE) return false;
    return true;
  }

  logout(): void {
    this.userStateService.logout();
    this.tenantStateService.logout();
  }

  isPageLoading(buttonState: boolean[]): boolean {
    return buttonState.some(b => b === true);
  }

  isUserAuthorized(requiredRoles: ERoleName[], userRole: ERoleName | undefined): boolean {
    return !!userRole && requiredRoles.includes(userRole);
  }

  generateKibanaUrl({
    baseUrl,
    dashboardId,
    id,
    keyword,
    label,
    timeRange = { from: 'now-24h/h', to: 'now' },
    hideHeader = true,
    hideFilterBar = true,
    showTimeFilter = true
  }: KibanaUrlParams): string {
    const gParam = rison.encode({
      refreshInterval: { pause: true, value: 60000 },
      time: timeRange
    });

    let url = `${baseUrl}/app/dashboards?auth_provider_hint=anonymous1#/view/${dashboardId}?embed=${hideHeader}&show-time-filter=${showTimeFilter}&_g=${gParam}&hide-filter-bar=${hideFilterBar}`;

    if ((id && keyword) || label) {
      const filters = [
        {
          meta: {
            key: keyword ? `${keyword}.keyword` : `labels.${label}`,
            params: { query: id },
            type: 'phrase'
          },
          query: {
            match_phrase: {
              [keyword ? `${keyword}.keyword` : `labels.${label}`]: id
            }
          }
        }
      ];

      const aParam = rison.encode({ filters });
      url += `&_a=${aParam}`;
    }

    return url;
  }

  sanitizeUrl(url: string): SafeResourceUrl {
    return this.sanitizer.bypassSecurityTrustResourceUrl(url);
  }
}
